import React, { useState } from 'react';
import { CheckCircle, ExternalLink, FileText } from 'lucide-react';
import Modal from './Modal';
import Button from './elements/Button';
import AcknowledgementChecklist from './AcknowledgementChecklist';
import { allAcknowledged, emptyAcknowledgements } from '../utils/signupAcknowledgements';

const ConsentModal = ({ isOpen, onConsent, onReject, isLoading }) => {
    // C3: the same three acknowledgements signup collects, asked the same way. Re-accepting
    // a new notice, a new contract and a restated age declaration on one tick would be the
    // same bundling as before, just later in the relationship.
    const [acknowledgements, setAcknowledgements] = useState(emptyAcknowledgements);
    const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@eternallocker.com';

    const handleConsent = () => {
        if (allAcknowledged(acknowledgements)) {
            onConsent(acknowledgements);
        }
    };

    const policyLinks = [
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Cancellation & Refunds', href: '/cancellation-refunds' },
        { label: 'Grievance Redressal', href: '/grievance-redressal' },
        { label: 'Nominee Access Policy', href: '/nominee-access-info' },
    ];

    return (
        <Modal open={isOpen} title="Terms & Privacy" onClose={onReject} icon={<CheckCircle />} closeButton={false}>
            <div className="w-full max-w-2xl p-2">
                    <p className="mb-4 text-sm leading-6 text-primary-dark">
                        Our notice or terms have changed since you last accepted. Please review and
                        accept each item to continue using Eternal Locker.
                    </p>

                    <div className="mb-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Review Required Documents</p>
                        <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0">
                            {policyLinks.map((policy) => (
                                <li key={policy.href}>
                                    <a
                                        href={policy.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex items-center justify-between rounded-lg border border-accent-main bg-background-light text-accent-main px-3 py-2 text-sm font-medium transition hover:border-accent-main hover:bg-primary-light"
                                    >
                                        <span className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-accent-main" />
                                            {policy.label}
                                        </span>
                                        <ExternalLink className="h-4 w-4 text-text-muted transition group-hover:text-accent-main" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mb-3 rounded-lg border border-accent-main bg-primary-light/50 p-3">
                        <AcknowledgementChecklist
                            acknowledgements={acknowledgements}
                            onChange={setAcknowledgements}
                            disabled={isLoading}
                            idPrefix="reconsent"
                        />
                    </div>

                    <p className="mb-5 text-xs text-text-muted">
                        Need help? Contact <a href={`mailto:${supportEmail}`} className="font-medium text-accent-main hover:underline">{supportEmail}</a>
                    </p>

                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button
                            type="secondary"
                            onClick={onReject}
                            disabled={isLoading}
                            className="w-full sm:w-auto"
                        >
                            Decline
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleConsent}
                            disabled={!allAcknowledged(acknowledgements) || isLoading}
                            loading={isLoading}
                            className="w-full sm:w-auto"
                        >
                            Accept and Continue
                        </Button>
                    </div>

                    <p className="mt-3 text-center text-xs text-text-muted">
                        Please accept to continue using Eternal Locker. Your data is secure and we respect your privacy. For any questions, reach out to us at <a href={`mailto:${supportEmail}`} className="font-medium text-accent-main hover:underline">{supportEmail}</a>.
                    </p>
                </div>
        </Modal>
    );
};

export default ConsentModal;
